import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { GroupSummary, GroupDetail } from '../types';
import {
  fetchGroups,
  fetchGroupDetail,
  createGroup as apiCreateGroup,
  deleteGroup as apiDeleteGroup,
  addToGroup as apiAddToGroup,
  removeFromGroup as apiRemoveFromGroup,
  updateGroupBatch as apiUpdateGroupBatch,
} from '../composables/useApi';

export const useGroupStore = defineStore('group', () => {
  const customGroups = ref<GroupSummary[]>([]);
  const currentGroup = ref<GroupDetail | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Auto-groups are read-only and computed on-demand
  const dateGroups = ref<GroupSummary[]>([]);
  const typeGroups = ref<GroupSummary[]>([]);

  async function loadGroups() {
    loading.value = true;
    try {
      const data = await fetchGroups();
      customGroups.value = data.custom;
      dateGroups.value = data.date;
      typeGroups.value = data.type;
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
    } finally {
      loading.value = false;
    }
  }

  async function openGroup(id: string) {
    loading.value = true;
    try {
      const detail = await fetchGroupDetail(id);
      currentGroup.value = detail;
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
    } finally {
      loading.value = false;
    }
  }

  async function createGroup(name: string, description?: string) {
    try {
      const result = await apiCreateGroup(name, description);
      await loadGroups();
      return result;
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      throw e;
    }
  }

  async function deleteGroup(id: string) {
    try {
      await apiDeleteGroup(id);
      await loadGroups();
      if (currentGroup.value?.id === id) {
        currentGroup.value = null;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      throw e;
    }
  }

  async function addVideoToGroup(groupId: string, fileId: string) {
    try {
      await apiAddToGroup(groupId, fileId);
      if (currentGroup.value?.id === groupId) {
        await openGroup(groupId);
      }
      await loadGroups();
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      throw e;
    }
  }

  async function removeVideoFromGroup(groupId: string, fileId: string) {
    try {
      await apiRemoveFromGroup(groupId, fileId);
      if (currentGroup.value?.id === groupId) {
        await openGroup(groupId);
      }
      await loadGroups();
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      throw e;
    }
  }

  async function batchRemoveVideos(groupId: string, fileIds: string[]) {
    try {
      await apiUpdateGroupBatch(groupId, { remove_file_ids: fileIds });
      if (currentGroup.value?.id === groupId) {
        await openGroup(groupId);
      }
      await loadGroups();
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      throw e;
    }
  }

  async function batchAddVideos(groupId: string, fileIds: string[]) {
    try {
      await apiUpdateGroupBatch(groupId, { add_file_ids: fileIds });
      if (currentGroup.value?.id === groupId) {
        await openGroup(groupId);
      }
      await loadGroups();
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      throw e;
    }
  }

  function clearCurrentGroup() {
    currentGroup.value = null;
  }

  return {
    customGroups,
    currentGroup,
    dateGroups,
    typeGroups,
    loading,
    error,
    loadGroups,
    openGroup,
    createGroup,
    deleteGroup,
    addVideoToGroup,
    removeVideoFromGroup,
    batchRemoveVideos,
    batchAddVideos,
    clearCurrentGroup,
  };
});
